import { useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, PencilSimple, WarningCircle, CheckCircle, CalendarBlank } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate } from "../serviceApi";
import { FREQ_LABEL } from "./useRecurring";

const Row = ({ label, children, testId }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-widest font-bold text-orange-700/70">{label}</div>
    <div className="text-sm text-slate-800 font-medium" data-testid={testId}>{children}</div>
  </div>
);

function SetStartDate({ s, onChanged }) {
  const [d, setD] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/service/recurring/schedules/${s.id}/first-occurrence`, { first_occurrence: d }); toast.success("Start date saved"); onChanged(); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not save the start date")); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs text-amber-800" data-testid="recurring-set-start-prompt">
      <CalendarBlank size={14} /> Set start date — when did this recurring service first occur?
      <Input type="date" value={d} max={s.next_due} onChange={(e) => setD(e.target.value)} className="h-7 w-36 bg-white" data-testid="recurring-set-start-input" />
      <Button size="sm" className="h-7 bg-orange-500 hover:bg-orange-600 text-white" disabled={!d || busy} onClick={save} data-testid="recurring-set-start-save">Save</Button>
    </div>
  );
}

// Read-only summary of a recurring series in the right-side panel; only Edit opens the full schedule modal.
export function RecurringPanel({ schedule: s, canManage, onEdit, onOpenRecord, onChanged }) {
  const done = (s.history || []).filter((h) => h.status === "completed").reverse();
  return (
    <section className={`rounded-lg border p-3 space-y-3 ${s.overdue ? "border-rose-300 bg-rose-50/60" : "border-orange-200 bg-orange-50/50"}`} data-testid="detail-recurring-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <ArrowsClockwise size={15} weight="bold" className="text-orange-500" />
        <span className="text-[11px] uppercase tracking-widest font-bold text-orange-700">Recurring service</span>
        {s.overdue && <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded text-[10px] font-bold uppercase bg-rose-600 text-white" data-testid="detail-recurring-overdue"><WarningCircle size={11} weight="fill" />Overdue since {fmtDate(s.next_due)}</span>}
        {s.status !== "active" && <span className="px-1.5 py-[1px] rounded text-[10px] font-bold uppercase bg-slate-200 text-slate-600">{s.status}</span>}
        {canManage && <Button size="sm" variant="outline" onClick={onEdit} className="ml-auto h-7 text-orange-700 border-orange-300 hover:bg-orange-100" data-testid="detail-recurring-edit-btn"><PencilSimple size={13} className="mr-1" />Edit</Button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-2">
        <Row label="Frequency" testId="detail-recurring-frequency">{FREQ_LABEL[s.frequency]} · reminds {s.lead_days} days before</Row>
        <Row label="First occurrence" testId="detail-recurring-first">{s.first_occurrence ? fmtDate(s.first_occurrence) : <span className="text-amber-700">Not set</span>}</Row>
        <Row label={s.overdue ? "Was due" : "Next due"} testId="detail-recurring-next-due"><span className={s.overdue ? "text-rose-700 font-bold" : ""}>{fmtDate(s.next_due)}</span></Row>
        <Row label="Category" testId="detail-recurring-category">{s.category_name || "—"}{s.unit ? ` · ${s.unit}` : ""}</Row>
        <Row label="Status" testId="detail-recurring-status">{s.overdue ? "Overdue — not completed" : s.status === "active" ? "Upcoming" : s.status}</Row>
        <Row label="Notify by">{(s.channels || []).join(" + ").toUpperCase()}</Row>
      </div>
      {s.needs_start_date && canManage && <SetStartDate s={s} onChanged={onChanged} />}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-orange-700/70 mb-1">Prior completed occurrences ({done.length})</div>
        {done.length === 0 ? <div className="text-xs text-slate-500" data-testid="detail-recurring-no-history">None recorded yet.</div> : (
          <ul className="space-y-1" data-testid="detail-recurring-history">
            {done.map((h) => (
              <li key={h.due_date} className="text-xs flex items-center gap-1.5">
                <CheckCircle size={13} weight="fill" className="text-emerald-600" /> Due {fmtDate(h.due_date)}
                {h.record_id && <button type="button" onClick={() => onOpenRecord(h.record_id)} className="text-[#327de3] hover:underline" data-testid={`detail-recurring-occ-${h.due_date}`}>open entry</button>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
